import { PlusOutlined } from '@ant-design/icons'
import { Button, Text } from '@lmc/ui-kit/src/general'
import type { BodyMapPickerReport } from '@lmc/validations'
import { message } from 'antd'
import useBodyMaps from 'hooks/useBodyMaps'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import AddBodyMapsDrawer from './AddBodyMapsDrawer'
import BodyMapItem from './BodyMapItem'
import RemoveBodyMapModal from './RemoveBodyMapModal'

export type RelatedBodyMapsInputProps = {
  /** The v2 form names the field with its own hint and gives the drawer a Back link. */
  isInStepDrawer?: boolean
  onChange?: (value: string[]) => void
  residentId?: string
  testId?: string
  value?: string[]
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

// Without this the guidance renders in near-black while the form theme greys
// the label out, so it out-shouts the thing it describes.
const Guidance = styled.div`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AddButtonRow = styled.div`
  display: flex;
`

/**
 * Owns `value` / `onChange` like UploadAttachmentsOnSaveInput, so it drops into
 * a FormItem directly.
 *
 * Titles come from useBodyMaps rather than the picker endpoint because that one
 * is filtered to unresolved reports, so a body map linked before it was resolved
 * would render blank.
 */
const RelatedBodyMapsInput = ({
  isInStepDrawer = false,
  onChange = () => {},
  residentId,
  testId,
  value = []
}: RelatedBodyMapsInputProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { bodyMaps } = useBodyMaps({ residentId })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [pendingRemovalId, setPendingRemovalId] = useState<string | null>(null)

  // An id with no matching body map is skipped rather than rendered blank.
  const linked = value
    .map((id) => bodyMaps.find((bodyMap) => bodyMap.id === id))
    .filter((bodyMap): bodyMap is (typeof bodyMaps)[number] => !!bodyMap)

  const pendingRemoval = linked.find(
    (bodyMap) => String(bodyMap.id) === pendingRemovalId
  )

  const confirmRemoval = () => {
    onChange(value.filter((id) => id !== pendingRemovalId))
    setPendingRemovalId(null)
    message.success(t(incidentReportsConstants.BODY_MAP_REMOVED))
  }

  return (
    <Wrapper data-testid={testId}>
      {!isInStepDrawer && (
        <Guidance>
          <Text level={1} weight={'regular'}>
            {t(incidentReportsConstants.ADD_BODY_MAPS_DESCRIPTION)}
          </Text>
        </Guidance>
      )}

      {linked.length > 0 && (
        <Rows>
          {linked.map((bodyMap) => (
            <BodyMapItem
              key={bodyMap.id}
              onRemove={setPendingRemovalId}
              report={
                {
                  // useBodyMaps returns the ts-common shape; normalise to the
                  // picker's so one row component serves both.
                  createdOn: new Date(bodyMap.createdOn).toISOString(),
                  direction: null,
                  id: String(bodyMap.id),
                  lastActivityOn: null,
                  lastComment: null,
                  lastDescription: null,
                  lastLoggedBy: null,
                  resolved: Boolean(bodyMap.resolved),
                  section: null,
                  title: bodyMap.title ?? null
                } satisfies BodyMapPickerReport
              }
              showCheckbox={false}
              showRemove
              testId={testId && `${testId}-item-${bodyMap.id}`}
            />
          ))}
        </Rows>
      )}

      <AddButtonRow>
        <Button
          data-testid={testId && `${testId}-add`}
          icon={<PlusOutlined />}
          onClick={() => setIsDrawerOpen(true)}
          variant="default"
        >
          {t(incidentReportsConstants.ADD_BODY_MAPS)}
        </Button>
      </AddButtonRow>

      <AddBodyMapsDrawer
        hasBackLink={isInStepDrawer}
        isOpen={isDrawerOpen}
        onAdd={(reportIds) => {
          onChange(reportIds)
          setIsDrawerOpen(false)
          // The panel also commits removals, so only toast a real addition — and
          // a session can add one map while removing another, which leaves the
          // length unchanged.
          if (reportIds.some((id) => !value.includes(id))) {
            message.success(t(incidentReportsConstants.BODY_MAP_ADDED))
          }
        }}
        onClose={() => setIsDrawerOpen(false)}
        residentId={residentId}
        selectedIds={value}
        testId={testId && `${testId}-drawer`}
      />

      {pendingRemoval && (
        <RemoveBodyMapModal
          bodyMap={{
            createdOn: new Date(pendingRemoval.createdOn).toISOString(),
            id: String(pendingRemoval.id),
            lastActivityOn: null,
            title: pendingRemoval.title ?? null
          }}
          isOpen
          onCancel={() => setPendingRemovalId(null)}
          onConfirm={confirmRemoval}
          testId={testId && `${testId}-remove-modal`}
        />
      )}
    </Wrapper>
  )
}

export default RelatedBodyMapsInput
